'use client'

import { CoworkerAvatar } from '@/components/shell'
import { cn } from '@/lib/utils'

/**
 * Who is speaking (an AI Employee), a headline, a finding and the next step,
 * restored from Driver-Auto-CC (`2a4551d^:.../approver/SpeakerHero.tsx`) and
 * generalised (plan 08 WP-VIS-06): the persona photo is a prop instead of a
 * Driver context, the eyebrow and heading level are the caller's, and
 * `children` carries extra lines (requester, a quoted note). The copy comes
 * from the server, never from client templates (Rule 10).
 */
export function SpeakerHero({
  speakerName,
  speakerRole,
  speakerPhoto,
  eyebrow,
  headline,
  headingLevel = 'h2',
  finding,
  points,
  next,
  actions,
  children,
  className,
}: {
  speakerName: string
  /** Shown after the name, e.g. "AI-generated summary". Omit to show the name only. */
  speakerRole?: string | null
  speakerPhoto?: string | null
  /** A small uppercase label above everything, e.g. "What is asked of you". */
  eyebrow?: string | null
  headline: string
  headingLevel?: 'h1' | 'h2' | 'h3'
  finding?: string | null
  points?: string[] | null
  next?: string | null
  actions?: React.ReactNode
  children?: React.ReactNode
  className?: string
}) {
  const Heading = headingLevel
  return (
    <section
      aria-label={eyebrow ?? `${speakerName}${speakerRole ? `, ${speakerRole}` : ''}`}
      className={cn(
        'flex flex-col gap-4 rounded-xl border border-hairline bg-card p-5 shadow-soft sm:flex-row sm:items-start',
        className
      )}
    >
      <div className='flex min-w-0 flex-1 gap-3'>
        <span aria-hidden='true' className='flex shrink-0'>
          <CoworkerAvatar name={speakerName} photo={speakerPhoto} size={40} alt='' />
        </span>
        <div className='min-w-0 space-y-1.5'>
          {eyebrow && <p className='text-micro uppercase text-muted-foreground'>{eyebrow}</p>}
          <p className='text-xs text-muted-foreground'>
            <span className='font-medium text-foreground'>{speakerName}</span>
            {speakerRole ? ` · ${speakerRole}` : ''}
          </p>
          <Heading className='font-display text-lg font-semibold text-foreground'>{headline}</Heading>
          {finding && <p className='text-sm text-foreground'>{finding}</p>}
          {points && points.length > 0 && (
            <ul className='list-disc space-y-1 pl-5 text-sm text-foreground'>
              {points.map((point, i) => (
                <li key={i}>{point}</li>
              ))}
            </ul>
          )}
          {next && <p className='text-sm text-muted-foreground'>{next}</p>}
          {children}
        </div>
      </div>
      {actions && <div className='flex shrink-0 flex-wrap gap-2 sm:flex-col'>{actions}</div>}
    </section>
  )
}
